import { DateString } from '../../../common/src/date_string.ts';
import {
  ClientOrg,
  defaultVoiceValues,
  Favorite,
  Household,
  HouseholdSurveyResponses,
  LearnProgress,
  OrgBase,
  OrgHymnal,
  OrgId,
  OrgInfo,
  SchoolTerm,
  SongList,
  SongViewWithOrg,
  Subscription,
  VoiceValues,
} from '../../../common/src/model.ts';
import { Pages } from '../../../common/src/pages.ts';

export const Guest = 'guest';
export const OnboardingLocalStoragePrefix = 'onboarding/';
const PrefixLength = 8;

export enum LocalStorageKey {
  AllSongLists = 'all-song-lists',
  AutoPlayNextSong = 'auto-play-next-song',
  BundleHash = 'bundle-hash',
  CustomMusicHash = 'custom-music-hash',
  CurrentOrgId = 'current-org-id',
  HymnalsHash = 'hymnals-hash',
  CacheRefreshVersion = 'cache-refresh-version',
  ClientSubscription = 'billing/client-subscription',
  CurrentHymnalName = 'current-hymnal-name',
  Favorites = 'favorites',
  FavoritesAdded = 'favorites-added',
  FavoritesRemoved = 'favorites-removed',
  FavoritesHash = 'favorites-hash',
  Household = 'household',
  CachedOrgs = 'cached-orgs',
  // TODO(remove after 2026-12-18): iOS app versions 4.x post their version here through the
  // getAppVersion reply; 24.0 and later report it in the user agent (see util/containing_app.ts)
  IOSAppVersion = 'ios-app-version',
  KeepScreenAwake = 'keep-screen-awake', // per-device: hold a screen wake lock while a song is loaded (issue #3608); never user-prefixed
  Logging = 'logging',
  MusicUploadSongName = 'music-upload-song-name',
  MusicUploadPsalmNumber = 'music-upload-psalm-number',
  MostRecentlyViewedOrgEvents = 'most-recently-viewed-org-events',
  MostRecentFooterTab = 'most-recent-footer-tab',
  MostRecentTodayTabVisit = 'most-recent-today-tab-visit',
  MostRecentOrgSyncTimestamp = 'most-recent-org-sync-timestamp',
  OnboardingHouseholdEmail = 'onboarding/household-email',
  OnboardingLastName = 'onboarding/last-name', // a temporary cache between onboarding screens
  OnboardingOrg = 'onboarding/org', // TODO(hewitt): No longer needed since we ask for org at the end of onboarding
  OnboardingHouseholdSurveyResponses = 'onboarding/household-survey-responses',
  OnboardingMatchingHousehold = 'onboarding/matching-household',
  OrgInfo = 'org-info',
  OrgHymnal = 'org-hymnal',
  OrgHymnalHash = 'org-hymnal-hash',
  // indicates an org hymnal download in progress (the Library's synchronizing indicator).
  // No longer a reentrancy guard (issue #3517) - a stale value from an older bundle is harmless
  // and is removed at startup.
  OrgHymnalDownloadHashingHash = 'org-hymnal-downloading-hash',
  // TODO(remove after 2027-04-06): the marker of reverifyOrgHymnalDownloadOnce; delete with it
  OrgHymnalDownloadReverified = 'org-hymnal-download-reverified',
  OptimisticSubscription = 'optimistic-subscription',
  PdfAutoDisplay = 'pdf-auto-display',
  Playlist = 'playlist',
  PlaylistIndex = 'playlist-index',
  ProspectiveSubscriptionOriginPage = 'prospective-subscription-origin-page',
  ProspectiveSubscriptionPlaylist = 'prospective-subscription-playlist',
  ProspectiveSubscriptionPlaylistIndex = 'prospective-subscription-playlist-index',
  PushHousehold = 'pushHousehold',
  IsMixerVisible = 'is-mixer-visible',
  IsPlayerMaximized = 'is-player-maximized',
  LearnProgressPending = 'learn-progress-pending', // write-behind copy of user.learn (see use_learn_progress.tsx)
  RhythmVoiceCalibration = 'rhythm-voice-calibration', // per-device voice latency offset for Sing Rhythm; never user-prefixed, like RhythmCalibration
  RhythmCalibration = 'rhythm-calibration', // per-device tap latency offset for Rhythm Echo; never user-prefixed (see pages/learn/audio/learn_audio_session.ts)
  PitchMatchSingAlong = 'pitch-match-sing-along', // per-device sing-along choice for Pitch Match; never user-prefixed (see pages/learn/pitch/pitch_mode.ts)
  SolfegeSingAlong = 'solfege-sing-along', // per-device sing-along choice for Sing in Solfège; never user-prefixed (see pages/learn/solfege/sing_in_solfege_page.tsx)
  Repeat = 'repeat',
  SchoolTerms = 'school-terms',
  ShowVocalsByDefault = 'show-vocals-by-default',
  Appearance = 'appearance', // only affects PDF Appearance for now (see references to isAppearancePDFSpecific below)
  SongIntroduction = 'song-intro',
  SongViews = 'song-views',
  SongListRequested = 'song-list-requested',
  ActiveUserToken = 'active-user-token',
  VerseCount = 'verse-count',
  VoiceValues = 'voice-values-new',
  WebAudioPlayback = 'web-audio-playback', // per-device: MIDI songs through Web Audio (issue #3718); never user-prefixed
  WeeklySongList = 'weekly-song-list',
  TooltipVisibilityCount = 'tooltip-visibility-count',
  SettingsTipSeen = 'settings-tip-seen', // one-time "Looking for settings?" callout under the header avatar
}

export const isAppearancePDFSpecific = true;

export enum AppearanceSetting {
  Auto = 'auto',
  Light = 'light',
  Dark = 'dark',
}

export function getAppearanceName(appearance?: AppearanceSetting): string {
  switch (appearance) {
    case AppearanceSetting.Light:
      return 'Light';
    case AppearanceSetting.Dark:
      return 'Dark';
    default:
      return 'Auto';
  }
}

export enum RepeatMode {
  Off = 'off',
  SongList = 'song-list',
  Song = 'song',
}

export enum SongIntroduction {
  LastFiveSeconds = 'last-five-seconds',
  LastTenSeconds = 'last-ten-seconds',
  LastFifteenSeconds = 'last-fifteen-seconds',
  FullSong = 'full-song',
}

export function getSongIntroName(songIntroduction?: SongIntroduction): string {
  switch (songIntroduction) {
    case SongIntroduction.LastFiveSeconds:
      return 'Last 5 Seconds';
    case SongIntroduction.LastTenSeconds:
      return 'Last 10 Seconds';
    case SongIntroduction.LastFifteenSeconds:
      return 'Last 15 Seconds';
    case SongIntroduction.FullSong:
      return 'Full Song';
    default:
      return 'None';
  }
}

interface TooltipVisibilityCount {
  switchOrganization?: number;
  longPress?: number;
  schoolAssignment?: number;
  mixerButton?: number;
}

export const usabilityTooltipDisplayCount = 3;

// The Rhythm Echo tap calibration (issue #3473): how many ms late this device's taps read against
// the sounds it plays. A property of the device (touch latency plus audio output latency), so it is
// stored once per device and shared by every household member; it must never move to the user.
// Sing Rhythm's voice calibration (issue #3723) has the same shape under a key of its own: how many
// ms late a sung note reads (audio output latency plus microphone input latency), which is not the
// tap's number.
export interface RhythmCalibration {
  offsetMs: number;
  measuredAt: string; // ISO timestamp
}

interface TypeMap {
  [LocalStorageKey.AllSongLists]: SongList[];
  [LocalStorageKey.AutoPlayNextSong]: boolean | undefined;
  [LocalStorageKey.BundleHash]: string | undefined;
  [LocalStorageKey.CacheRefreshVersion]: number;
  [LocalStorageKey.Appearance]: AppearanceSetting;
  [LocalStorageKey.CustomMusicHash]: string | undefined;
  [LocalStorageKey.CurrentOrgId]: OrgId | undefined;
  [LocalStorageKey.HymnalsHash]: string | undefined;
  [LocalStorageKey.CachedOrgs]: ClientOrg[] | []; // only add, never remove
  [LocalStorageKey.ClientSubscription]: (Subscription & { appAccountToken?: string }) | undefined;
  [LocalStorageKey.CurrentHymnalName]: string | undefined;
  [LocalStorageKey.Favorites]: Favorite[];
  [LocalStorageKey.FavoritesAdded]: Favorite[];
  [LocalStorageKey.FavoritesRemoved]: Favorite[];
  [LocalStorageKey.FavoritesHash]: string | undefined;
  [LocalStorageKey.Household]: Household | undefined;
  [LocalStorageKey.IOSAppVersion]: string | undefined;
  [LocalStorageKey.IsMixerVisible]: boolean;
  [LocalStorageKey.IsPlayerMaximized]: boolean;
  [LocalStorageKey.KeepScreenAwake]: boolean;
  [LocalStorageKey.LearnProgressPending]: LearnProgress | undefined;
  [LocalStorageKey.RhythmCalibration]: RhythmCalibration | undefined;
  [LocalStorageKey.RhythmVoiceCalibration]: RhythmCalibration | undefined;
  [LocalStorageKey.PitchMatchSingAlong]: boolean | undefined;
  [LocalStorageKey.SolfegeSingAlong]: boolean | undefined;
  [LocalStorageKey.Logging]: string[];
  [LocalStorageKey.MusicUploadSongName]: string | undefined;
  [LocalStorageKey.MusicUploadPsalmNumber]: number | undefined;
  [LocalStorageKey.MostRecentlyViewedOrgEvents]: SongList[];
  [LocalStorageKey.MostRecentFooterTab]: Pages;
  [LocalStorageKey.MostRecentTodayTabVisit]: DateString | undefined;
  [LocalStorageKey.MostRecentOrgSyncTimestamp]: number | undefined;
  [LocalStorageKey.OnboardingHouseholdEmail]: string | undefined;
  [LocalStorageKey.OnboardingLastName]: string | undefined;
  [LocalStorageKey.OnboardingOrg]: OrgBase | undefined;
  [LocalStorageKey.OnboardingHouseholdSurveyResponses]: HouseholdSurveyResponses;
  [LocalStorageKey.OnboardingMatchingHousehold]: Household | undefined;
  [LocalStorageKey.OrgInfo]: OrgInfo | undefined;
  [LocalStorageKey.OrgHymnal]: OrgHymnal;
  [LocalStorageKey.OrgHymnalHash]: string | undefined;
  [LocalStorageKey.OrgHymnalDownloadHashingHash]: string | undefined;
  [LocalStorageKey.OrgHymnalDownloadReverified]: boolean | undefined;
  [LocalStorageKey.OptimisticSubscription]:
    | {
        householdToken: string;
        sessionId: string;
        createdAtMs: number;
      }
    | undefined;
  [LocalStorageKey.PdfAutoDisplay]: boolean;
  [LocalStorageKey.Playlist]: SongList | undefined;
  [LocalStorageKey.PlaylistIndex]: number;
  [LocalStorageKey.ProspectiveSubscriptionOriginPage]: Pages | undefined;
  [LocalStorageKey.ProspectiveSubscriptionPlaylist]: SongList | undefined;
  [LocalStorageKey.ProspectiveSubscriptionPlaylistIndex]: number;
  [LocalStorageKey.PushHousehold]: boolean;
  [LocalStorageKey.Repeat]: RepeatMode;
  [LocalStorageKey.SchoolTerms]: SchoolTerm[];
  [LocalStorageKey.ShowVocalsByDefault]: boolean;
  [LocalStorageKey.SongIntroduction]: SongIntroduction | undefined;
  [LocalStorageKey.SongListRequested]: DateString | undefined;
  [LocalStorageKey.SongViews]: SongViewWithOrg[];
  [LocalStorageKey.ActiveUserToken]: string | undefined;
  [LocalStorageKey.VerseCount]: number | undefined;
  [LocalStorageKey.VoiceValues]: VoiceValues;
  [LocalStorageKey.WebAudioPlayback]: boolean;
  [LocalStorageKey.WeeklySongList]: SongList[];
  [LocalStorageKey.TooltipVisibilityCount]: TooltipVisibilityCount;
  [LocalStorageKey.SettingsTipSeen]: boolean;
}

export const defaultVerseCount = 4; // allows defaulting to the hymn verse count

// TODO(hewitt): Freeze all objects & lists in this array (freezing the top level object does not prevent this)
const DefaultValues: { [key in LocalStorageKey]: LocalStorageType<key> } = Object.freeze({
  [LocalStorageKey.AllSongLists]: [],
  [LocalStorageKey.AutoPlayNextSong]: undefined,
  [LocalStorageKey.BundleHash]: undefined,
  [LocalStorageKey.CacheRefreshVersion]: 0,
  [LocalStorageKey.Appearance]: AppearanceSetting.Light, // PDF-only appearance for now
  [LocalStorageKey.CustomMusicHash]: undefined,
  [LocalStorageKey.CurrentOrgId]: undefined,
  [LocalStorageKey.HymnalsHash]: undefined,
  [LocalStorageKey.CachedOrgs]: [],
  [LocalStorageKey.ClientSubscription]: undefined,
  [LocalStorageKey.CurrentHymnalName]: undefined,
  [LocalStorageKey.Favorites]: [],
  [LocalStorageKey.FavoritesAdded]: [],
  [LocalStorageKey.FavoritesRemoved]: [],
  [LocalStorageKey.FavoritesHash]: undefined,
  [LocalStorageKey.Household]: undefined,
  [LocalStorageKey.IOSAppVersion]: undefined,
  [LocalStorageKey.IsMixerVisible]: true,
  [LocalStorageKey.IsPlayerMaximized]: false,
  [LocalStorageKey.KeepScreenAwake]: false, // off until the household turns it on: an always-on screen is a battery cost most devices never need (issue #3608)
  [LocalStorageKey.LearnProgressPending]: undefined,
  [LocalStorageKey.RhythmCalibration]: undefined,
  [LocalStorageKey.RhythmVoiceCalibration]: undefined,
  [LocalStorageKey.PitchMatchSingAlong]: undefined, // unset until the singer moves the switch: the device default applies
  [LocalStorageKey.SolfegeSingAlong]: undefined, // unset until the singer moves the switch: off, on every device
  [LocalStorageKey.Logging]: [],
  [LocalStorageKey.MusicUploadSongName]: undefined,
  [LocalStorageKey.MusicUploadPsalmNumber]: undefined,
  [LocalStorageKey.MostRecentlyViewedOrgEvents]: [],
  [LocalStorageKey.MostRecentFooterTab]: Pages.Organization,
  [LocalStorageKey.MostRecentTodayTabVisit]: undefined,
  [LocalStorageKey.MostRecentOrgSyncTimestamp]: undefined,
  [LocalStorageKey.OnboardingHouseholdEmail]: undefined,
  [LocalStorageKey.OnboardingLastName]: undefined,
  [LocalStorageKey.OnboardingOrg]: undefined,
  [LocalStorageKey.OnboardingHouseholdSurveyResponses]: {},
  [LocalStorageKey.OnboardingMatchingHousehold]: undefined,
  [LocalStorageKey.OrgInfo]: undefined,
  [LocalStorageKey.OrgHymnal]: { hash: 'empty', songs: [] },
  [LocalStorageKey.OrgHymnalHash]: undefined,
  [LocalStorageKey.OrgHymnalDownloadHashingHash]: undefined,
  [LocalStorageKey.OrgHymnalDownloadReverified]: undefined,
  [LocalStorageKey.OptimisticSubscription]: undefined,
  [LocalStorageKey.PdfAutoDisplay]: false,
  [LocalStorageKey.Playlist]: undefined,
  [LocalStorageKey.PlaylistIndex]: 0,
  [LocalStorageKey.ProspectiveSubscriptionOriginPage]: undefined,
  [LocalStorageKey.ProspectiveSubscriptionPlaylist]: undefined,
  [LocalStorageKey.ProspectiveSubscriptionPlaylistIndex]: 0,
  [LocalStorageKey.PushHousehold]: false,
  [LocalStorageKey.Repeat]: RepeatMode.Off,
  [LocalStorageKey.SchoolTerms]: [],
  [LocalStorageKey.SongListRequested]: undefined,
  [LocalStorageKey.SongViews]: [],
  [LocalStorageKey.ActiveUserToken]: undefined,
  [LocalStorageKey.VerseCount]: undefined,
  [LocalStorageKey.ShowVocalsByDefault]: false,
  [LocalStorageKey.VoiceValues]: defaultVoiceValues,
  [LocalStorageKey.WebAudioPlayback]: true, // on by default (issue #3842); a device whose user chose "No" keeps its stored false
  [LocalStorageKey.SongIntroduction]: undefined,
  [LocalStorageKey.WeeklySongList]: [],
  [LocalStorageKey.TooltipVisibilityCount]: {},
  [LocalStorageKey.SettingsTipSeen]: false,
});

const prefixedKeys = [
  LocalStorageKey.AutoPlayNextSong,
  LocalStorageKey.CurrentHymnalName,
  LocalStorageKey.Favorites,
  LocalStorageKey.FavoritesAdded,
  LocalStorageKey.FavoritesRemoved,
  LocalStorageKey.FavoritesHash,
  LocalStorageKey.IsMixerVisible,
  LocalStorageKey.IsPlayerMaximized,
  LocalStorageKey.LearnProgressPending,
  LocalStorageKey.PdfAutoDisplay,
  LocalStorageKey.Playlist,
  LocalStorageKey.PlaylistIndex,
  LocalStorageKey.ProspectiveSubscriptionOriginPage,
  LocalStorageKey.ProspectiveSubscriptionPlaylist,
  LocalStorageKey.ProspectiveSubscriptionPlaylistIndex,
  LocalStorageKey.MostRecentlyViewedOrgEvents,
  LocalStorageKey.MostRecentFooterTab,
  LocalStorageKey.MostRecentTodayTabVisit,
  LocalStorageKey.Repeat,
  LocalStorageKey.VerseCount,
  LocalStorageKey.ShowVocalsByDefault,
  LocalStorageKey.VoiceValues,
  LocalStorageKey.SongIntroduction,
  LocalStorageKey.Appearance,
];

export type LocalStorageType<T extends LocalStorageKey> = TypeMap[T];

function parseJson<T extends LocalStorageKey>(key: T, rawValue: string): LocalStorageType<T> {
  // Entries created by raw HTML pages were not originally JSON stringified... :(
  // Numbers & booleans are not wrapped in quotes even when JSON stringified.
  // These will cause JSON.parse() to throw an exception, in which case we just return the default value.
  try {
    return JSON.parse(rawValue) as LocalStorageType<T>;
  } catch {
    return getDefaultValue(key);
  }
}

export function registerForLocalStorageEvents() {
  // IMPORTANT: Not called for changes by our app -> only for changes to the cache outside the app.
  //            For example, if we manually delete a key or if two instances are running.
  window.addEventListener('storage', (event) => {
    if (!event.key) {
      return;
    }
    const oldValue = event.oldValue === null ? undefined : event.oldValue;
    const newValue = event.newValue === null ? undefined : event.newValue;
    keyChanged(event.key, oldValue, newValue);
  });
}

export function keyChanged(rawKey: string, oldValue?: any, newValue?: any) {
  let nonPrefixedKey: LocalStorageKey;
  const slashIndex = rawKey.indexOf('/');
  if (slashIndex !== -1) {
    const suffix = rawKey.slice(slashIndex + 1) as LocalStorageKey;
    nonPrefixedKey = prefixedKeys.includes(suffix) ? suffix : (rawKey as LocalStorageKey);
  } else {
    nonPrefixedKey = rawKey as LocalStorageKey;
  }
  if ((oldValue === undefined && newValue === undefined) || JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
    _callListeners(nonPrefixedKey, oldValue, newValue);
  }
}

export function localStorageGet<T extends LocalStorageKey>(
  key: T,
  defaultOverride?: LocalStorageType<T>,
): LocalStorageType<T> {
  const rawValue = _localStorageGetRaw(_getPrefixedLocalStorageName(key));
  if (rawValue !== undefined) {
    return parseJson(key, rawValue);
  }
  return getDefaultValue(key, defaultOverride);
}

export function getDefaultValue<T extends LocalStorageKey>(
  key: T,
  defaultOverride?: LocalStorageType<T>,
): LocalStorageType<T> {
  if (defaultOverride !== undefined) {
    return JSON.parse(JSON.stringify(defaultOverride));
  }
  const value = DefaultValues[key];
  if (value === undefined) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
}

export function localStorageSet<T extends LocalStorageKey>(key: T, value: LocalStorageType<T>) {
  if (value === undefined) {
    localStorageRemove(key); // calls listeners
    return;
  }
  const oldValue = _localStorageGetRaw(_getPrefixedLocalStorageName(key));
  const newValue = JSON.stringify(value);
  if (newValue !== oldValue) {
    _localStorageSetRaw(_getPrefixedLocalStorageName(key), newValue);
    _callListeners(key, oldValue !== undefined ? parseJson(key, oldValue) : oldValue, value);

    // invalidate oll user-specific settings when switching users
    if (key === LocalStorageKey.ActiveUserToken) {
      for (const key of Object.keys(listeners) as LocalStorageKey[]) {
        if (prefixedKeys.includes(key)) {
          _callListeners(key, undefined, localStorageGet(key));
        }
      }
    }
  }
}

export function localStorageRemove<T extends LocalStorageKey>(key: T) {
  const oldValue = _localStorageGetRaw(_getPrefixedLocalStorageName(key));
  if (oldValue !== undefined) {
    _localStorageRemoveRaw(_getPrefixedLocalStorageName(key));
    _callListeners(key, parseJson(key, oldValue));
  }
}

function _localStorageRemoveRaw(name: string) {
  localStorage.removeItem(name);
}

export function addListener<T extends LocalStorageKey>(key: T, listener: Listener): void {
  if (key in listeners) {
    listeners[key]!.push(listener);
  } else {
    listeners[key] = [listener];
  }
}

export function removeListener<T extends LocalStorageKey>(key: T, listener: Listener): void {
  const keyListeners = listeners[key];
  if (!keyListeners) {
    return;
  }
  const index = keyListeners.indexOf(listener);
  if (index !== -1) {
    keyListeners.splice(index, 1);
  }
}

export function clearClientLocalStorage() {
  localStorage.clear();
}

// NOTE: these raw storage keys may be prefixed with user email address, so are not necessarily LocalStorageKey values
export function getAllLocalStorageKeys(): string[] {
  return Object.keys(localStorage) as string[];
}

export function getAllLocalStorage(): any {
  return localStorage;
}

function _localStorageGetRaw(name: string): string | undefined {
  return localStorage.getItem(name) ?? undefined;
}

function _localStorageSetRaw(name: string, value: string) {
  localStorage.setItem(name, value);
}

function _getPrefixedLocalStorageName(key: LocalStorageKey): string {
  if (!prefixedKeys.includes(key)) {
    return key;
  }

  const token = localStorageGet(LocalStorageKey.ActiveUserToken);
  const prefix = token ? token.substring(0, PrefixLength) : Guest;
  return `${prefix}/${key}`;
}

function _callListeners<T extends LocalStorageKey>(
  key: T,
  _oldValue?: LocalStorageType<T>,
  _newValue?: LocalStorageType<T>,
) {
  if (!(key in listeners)) {
    return;
  }
  for (const listener of listeners[key] ?? []) {
    listener();
  }
}

// todo(hewitt): would be nice someday, but gathering up all old values when `user` changes is challenging
// export type ListenerArgs<T extends LocalStorageKey> = {
//   oldValue: LocalStorageType<T> | undefined;
//   newValue: LocalStorageType<T> | undefined;
// };
// export type Listener<T extends StorageKey> = (args: ListenerArgs<T>) => void;

export type Listener = () => void;

const listeners: Partial<{ [key in LocalStorageKey]: Listener[] }> = {};

// TODO(remove after 2026-12-01): deprecated with LocalStorageKey.IOSAppVersion (iOS 4.x only)
// Older iOS hosts wrote the app version as a JSON number literal (e.g. `6.5`), which
// JSON.parse then returned as a JS number. That caused naturalStringCompare crashes
// when calling .localeCompare on a number. Coerce any stored number to a string.
export function coerceIOSAppVersionToString() {
  const raw = _localStorageGetRaw(LocalStorageKey.IOSAppVersion);
  if (raw === undefined) return;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed === 'string') return;
    _localStorageSetRaw(LocalStorageKey.IOSAppVersion, JSON.stringify(String(parsed)));
  } catch {
    _localStorageRemoveRaw(LocalStorageKey.IOSAppVersion);
  }
}

// Permanent defense: WeeklySingingPlan requires `days`, but a small number of
// users have hit `t.days.find` crashes from a cached org whose singingPlan is
// missing it. Source of the malformed shape is unknown, so leave this in place
// indefinitely to strip any such plan at startup; a normal sync will refetch.
export function dropMalformedCachedSingingPlans() {
  const cachedOrgs = localStorageGet(LocalStorageKey.CachedOrgs);
  let changed = false;
  const cleaned = cachedOrgs.map((org) => {
    if (org.singingPlan && !Array.isArray(org.singingPlan.days)) {
      changed = true;
      const { singingPlan: _, ...rest } = org;
      return rest;
    }
    return org;
  });
  if (changed) {
    localStorageSet(LocalStorageKey.CachedOrgs, cleaned);
  }
}

// The org billing type flags were renamed for the customer-facing option names (GitHub issue #3406):
// isFreeOrg -> isHouseholdChoice and isSubsidized -> isUsageBased. Orgs cached by a client bundle from
// before the rename keep the retired keys until the next org sync (at most hourly), and in the meantime
// the banner and song-gate policy would read every org as Legacy - so the keys are renamed in place at
// startup instead. Values already stored under the new names win.
// TODO(remove after 2026-11-01): delete with this function, its call site in startup.ts and its test
export function renameCachedOrgBillingFlags() {
  const cachedOrgs = localStorageGet(LocalStorageKey.CachedOrgs);
  let changed = false;
  const renamed = cachedOrgs.map((org) => {
    const { isFreeOrg, isSubsidized, ...rest } = org as ClientOrg & { isFreeOrg?: boolean; isSubsidized?: boolean };
    if (isFreeOrg === undefined && isSubsidized === undefined) {
      return org;
    }
    changed = true;
    return {
      ...rest,
      ...(rest.isHouseholdChoice === undefined && isFreeOrg !== undefined && { isHouseholdChoice: isFreeOrg }),
      ...(rest.isUsageBased === undefined && isSubsidized !== undefined && { isUsageBased: isSubsidized }),
    };
  });
  if (changed) {
    localStorageSet(LocalStorageKey.CachedOrgs, renamed);
  }
}

// TODO(remove after 2027-04-06): one-time re-verification of the church's offline songs. Bundles
// before October 2026 recorded the org hymnal download as done when the songs were in fact
// skipped - the prerequisites were not ready, or the church's own custom music had not loaded
// yet - so devices that upgraded carry a hash that says "downloaded" over an incomplete cache
// and nothing re-checks until the church's hymnal changes. Clearing the hash once makes the next
// check redo the download; files already stored are cache hits. Runs once per device: the
// OrgHymnalDownloadReverified marker. Delete with that key, its type and default, the
// [org-hymnal-reverify] log line, and the test in data/test/reverify_org_hymnal_download_once.test.ts.
export function reverifyOrgHymnalDownloadOnce() {
  if (localStorageGet(LocalStorageKey.OrgHymnalDownloadReverified)) {
    return;
  }
  localStorageSet(LocalStorageKey.OrgHymnalDownloadReverified, true);
  if (localStorageGet(LocalStorageKey.OrgHymnalHash) === undefined) {
    return;
  }
  localStorageRemove(LocalStorageKey.OrgHymnalHash);
  console.log('[org-hymnal-reverify] cleared the org hymnal hash once so the church songs are re-verified');
}

export function upgradeRawKey(oldKey: string, newKey: string, getNewValue: (oldValue: string | undefined) => string) {
  _localStorageSetRaw(newKey, getNewValue(_localStorageGetRaw(oldKey)));
  _localStorageRemoveRaw(oldKey);
}
